import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Plus, Trash2, Clock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useMentorData, buildExperiences, todayLocal } from './mentorData';
import {
  MentorWorkspace, MentorLoading, NoMentorProfile,
  CompleteExperienceDialog, OfflineOpportunityDialog,
} from './MentorComponents';
import MentorExperienceTable from './MentorExperienceTable';
import { useOverflowFade } from '@/lib/useOverflowFade';

const CATEGORIES = [
  { id: 'online', label: 'Online Mentoring' },
  { id: 'offline', label: 'Workplace Visits' },
  { id: 'workshops', label: 'Workshops' },
  { id: 'internships', label: 'Internships' },
];

function ComingSoon({ label }) {
  return (
    <div className="rounded-2xl border border-dashed border-[#E5E7EB] p-12 text-center" data-testid="coming-soon">
      <Sparkles className="h-7 w-7 text-gray-200 mx-auto mb-3" />
      <p className="text-base font-bold text-[#1F2937] font-['Outfit'] mb-1">{label} — Coming Soon</p>
      <p className="text-sm text-[#9CA3AF]">We're building this experience type. You'll be able to offer {label.toLowerCase()} here soon.</p>
    </div>
  );
}

export default function MentorExperiencesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loading, profile, sessions, bookings, refresh } = useMentorData();
  const [completing, setCompleting] = useState(null);
  const [showOfflineDialog, setShowOfflineDialog] = useState(false);
  const catFade = useOverflowFade();

  const tab = CATEGORIES.some(c => c.id === searchParams.get('tab')) ? searchParams.get('tab') : 'online';
  const setTab = (t) => setSearchParams(t === 'online' ? {} : { tab: t });

  if (loading) return <MentorLoading />;
  if (!profile) return <NoMentorProfile />;

  const experiences = buildExperiences(sessions, bookings);
  const online = experiences.filter(e => !e.offline);
  const offlineCapsList = profile.offline_capabilities || [];
  // Older visits carry no stamped venue: fall back to the visit the mentor published, then their workplace
  const visitVenue = (e) => e.venue
    || offlineCapsList.find(c => c.date === e.date && (!e.careerSlug || c.career_slug === e.careerSlug))?.workplace_name
    || profile.company_name || '';
  const offlineBookings = experiences.filter(e => e.offline).map(e => ({ ...e, venue: visitVenue(e) }));

  const handleComplete = (e) => { if (e.source === 'session') navigate(e.link); else setCompleting(e); };

  const offlineCaps = offlineCapsList;
  const isApproved = profile.approval_status === 'approved';

  const removeOffline = async (index) => {
    try {
      await api.delete(`/mentor-profile/offline-capability/${index}`);
      toast.success('Workplace visit removed');
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove'); }
  };

  return (
    <MentorWorkspace testId="mentor-experiences-page">
      <h1 className="text-3xl sm:text-4xl font-bold text-[#1F2937] font-['Outfit'] tracking-tight mb-6">My Experiences</h1>

      {/* Category navigation */}
      <div className={`relative mb-7 ${catFade.faded ? "after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-10 after:bg-gradient-to-l after:from-white after:pointer-events-none" : ''}`}>
      <div ref={catFade.ref} className="flex gap-6 border-b border-[#EFF0F2] overflow-x-auto md:overflow-visible md:flex-wrap [&::-webkit-scrollbar]:hidden" data-testid="experience-categories">
        {CATEGORIES.map(c => (
          <button
            key={c.id}
            onClick={() => setTab(c.id)}
            className={`pt-2 pb-3 min-h-[44px] md:min-h-0 md:pt-0 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${
              tab === c.id ? 'border-[#F97D6A] text-[#1F2937]' : 'border-transparent text-[#9CA3AF] hover:text-[#6B7280]'
            }`}
            data-testid={`category-${c.id}`}
          >
            {c.label}
          </button>
        ))}
      </div>
      </div>

      {tab === 'online' && (
        <section data-testid="online-mentoring-section">
          <MentorExperienceTable experiences={online} onComplete={handleComplete}
            emptyLabel="No online sessions assigned to you yet." />
        </section>
      )}

      {tab === 'offline' && (
        <section className="space-y-10" data-testid="offline-experiences-section">
          {/* Opportunities offered */}
          <div>
            <div className="flex items-end justify-between gap-4 mb-4">
              <div>
                <h2 className="text-base md:text-lg font-bold text-[#1F2937] font-['Outfit']">Workplace Visit Offerings</h2>
                <p className="text-xs text-[#9CA3AF] mt-1">Workplace visits students can request. These are offerings, not confirmed bookings.</p>
              </div>
              {isApproved && (
                <Button onClick={() => setShowOfflineDialog(true)} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-full h-9 px-4 text-xs font-semibold shrink-0" data-testid="add-offline-btn">
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> New Opportunity
                </Button>
              )}
            </div>
            {offlineCaps.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[#E5E7EB] p-10 text-center" data-testid="offline-opportunities-empty">
                <MapPin className="h-6 w-6 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-[#9CA3AF]">You haven't offered any workplace visits yet.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="offline-opportunities">
                {offlineCaps.map((cap, i) => {
                  const careerName = (profile.mapped_careers || []).find(c => c.slug === cap.career_slug)?.name || cap.career_slug;
                  const isPast = cap.date && cap.date < todayLocal();
                  return (
                    <div key={i} className="rounded-2xl border border-[#EFF0F2] p-4 flex items-start gap-3" data-testid={`offline-cap-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold text-[#1F2937] font-['Outfit'] truncate">{careerName}</p>
                          <Badge className={`border-0 text-[9px] shrink-0 ${isPast ? 'bg-gray-100 text-gray-500' : 'bg-emerald-50 text-emerald-600'}`}>{isPast ? 'Past' : 'Open'}</Badge>
                        </div>
                        <p className="text-sm text-[#6B7280] mt-1">{cap.workplace_name} · {cap.city}</p>
                        {cap.date && (
                          <p className="text-[11px] text-[#9CA3AF] mt-0.5 flex items-center gap-1.5">
                            <Clock className="h-3 w-3" />{formatDate(cap.date)} · {cap.start_time}–{cap.end_time}
                          </p>
                        )}
                      </div>
                      {isApproved && (
                        <Button variant="ghost" size="sm" onClick={() => removeOffline(i)} className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full h-8 w-8 p-0 shrink-0" data-testid={`remove-offline-${i}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Student bookings for offline experiences */}
          <div>
            <div className="mb-4">
              <h2 className="text-base md:text-lg font-bold text-[#1F2937] font-['Outfit']">Student bookings</h2>
              <p className="text-xs text-[#9CA3AF] mt-1">Confirmed and requested workplace visits assigned to you.</p>
            </div>
            <MentorExperienceTable experiences={offlineBookings} offline onComplete={handleComplete}
              emptyLabel="No workplace visit bookings yet." />
          </div>
        </section>
      )}

      {tab === 'workshops' && <ComingSoon label="Workshops" />}
      {tab === 'internships' && <ComingSoon label="Internships" />}

      <CompleteExperienceDialog exp={completing} open={!!completing} onOpenChange={(o) => { if (!o) setCompleting(null); }} onDone={() => { setCompleting(null); refresh(); }} />
      <OfflineOpportunityDialog open={showOfflineDialog} onOpenChange={setShowOfflineDialog} careers={profile.mapped_careers} onDone={refresh} />
    </MentorWorkspace>
  );
}
